<template>
  <div
    v-if="bluredOrNotHidden"
    ref="mjSection"
    data-testid="mj-section"
    :class="{
      active: isActive,
      hovered: isHovered,
      'z-[4]': isActive && !modalIsOpen,
      'z-[5]': isHovered && !modalIsOpen,
      'section-borders': draggingStore.dragging,
      'dragged-opacity': false,
      '!bg-structure-locked':
        !isMovableForCurrentPermissions || (isSyncedRow && !isEditing),
      '!bg-structure-3': isEditing,
      hidden: hasVariants && !isActiveVariant,
    }"
    @mouseover="mouseover"
    @mouseleave="isHovered = false"
    @click="click($event)"
  >
    <TConfirmationModal ref="confirmationModalUnsaved" close-out-no success>
      <template #header>{{ $t("file_manager.warning") }}</template>
      {{ $t("forms.unsaved_synced") }}
      <template #confirm-action>{{ $t("layout.save") }}</template>
    </TConfirmationModal>

    <TConfirmationModal ref="syncedLangConfirmModal" close-out-no success>
      <template #header>{{ $t("file_manager.warning") }}</template>
      <p v-html="syncedLangConfirmMessage"></p>
      <template #confirm-action>{{ $t("layout.save") }}</template>
    </TConfirmationModal>

    <div class="flex" :style="{}">
      <div class="basis-4 bg-[var(--c-email-canvas)]" :style="{}"></div>
      <div class="relative flex-1" :style="{}">
        <div
          data-testid="template-bg"
          class="relative z-[1]"
          :class="{
            'pointer-events-none':
              !isMovableForCurrentPermissions || (isSyncedRow && !isEditing),
          }"
        >
          <div
            :class="{
              '!z-[10] !bg-structure-locked':
                !isMovableForCurrentPermissions || (isSyncedRow && !isEditing),
              '!z-[10] !bg-structure-3': isEditing,
            }"
          >
            <div
              v-if="layout == 'normal'"
              :style="{ ...sectionStylesDiv, ...hiddenStyle }"
              style="margin: 0px auto"
              @mouseover="contentHovered = true"
              @mouseleave="contentHovered = false"
            >
              <table
                data-testid="section-normal-layout"
                role="presentation"
                cellpadding="0"
                cellspacing="0"
                :style="normalTableStyles"
                style="
                  font-size: 0px;
                  width: 100%;
                  background-position: top center;
                "
                align="center"
                border="0"
              >
                <tbody>
                  <tr>
                    <td
                      :style="sectionStyles"
                      style="
                        text-align: center;
                        vertical-align: top;
                        direction: ltr;
                        font-size: 0px;
                      "
                    >
                      <mjmlColumn
                        v-for="(column, columnIndex) in section.children"
                        :key="`${column.uid}-${columnIndex}-${section.uid}`"
                        :column-index="columnIndex"
                        :section-index="index"
                        :section-locked="isLocked"
                        :is-synced="isSyncedRow"
                      ></mjmlColumn>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="layout == 'full'" :style="hiddenStyle">
              <table
                data-testid="section-full-layout"
                :style="fullWidthTableStyles"
                role="presentation"
                cellpadding="0"
                cellspacing="0"
                data-legacy-border="0"
                style="
                  font-size: 0px;
                  width: 100%;
                  background-position: top center;
                "
                border="0"
              >
                <tbody>
                  <tr>
                    <td>
                      <div
                        style="margin: 0px auto"
                        :style="{ maxWidth: bgWidth + 'px' }"
                        @mouseleave="contentHovered = false"
                      >
                        <table
                          role="presentation"
                          cellpadding="0"
                          cellspacing="0"
                          style="font-size: 0px; width: 100%"
                          align="center"
                          border="0"
                        >
                          <tbody>
                            <tr>
                              <td
                                :style="sectionStyles"
                                style="
                                  text-align: center;
                                  vertical-align: top;
                                  direction: ltr;
                                  font-size: 0px;
                                "
                              >
                                <mjmlColumn
                                  v-for="(
                                    column, columnIndex
                                  ) in section.children"
                                  :key="`${column.uid}-${columnIndex}-${section.uid}`"
                                  :column-index="columnIndex"
                                  :section-index="index"
                                  :section-locked="isLocked"
                                  :is-synced="isSyncedRow"
                                ></mjmlColumn>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div class="basis-4 bg-[var(--c-email-canvas)]"></div>
    </div>

    <CreateBlockAPI
      v-if="creatingSavedBlock && api.SAVED_SECTIONS"
      type="saved_section"
      :index="index"
      @create-end="closeSavedBlockModal"
    />
    <CreateBlockAPI
      v-if="creatingSynedRow && api.SAVED_SECTIONS"
      type="synced_section"
      :index="index"
      @create-end="syncedSection"
    />
    <sectionActions
      v-if="showSectionActions"
      :disable-up-movement="disableUpMovement"
      :disable-down-movement="disableDownMovement"
      :is-locked="isLocked"
      :section-uid="section.uid"
      :is-synced="isSyncedRow"
      :is-editing="isEditing"
      :show-paste="showPaste"
      :copied="copied"
      @duplicate="copySection"
      @copy="copyToClipboard"
      @unlink-synced="unlinkSyncedSection"
      @remove="deleteSection"
      @save="saveSection"
      @synced="createSyncedSection"
      @edit-synced="savedBlocksStore.editingRowUid = section.uid"
      @moveUpSection="moveUpSection"
      @moveDownSection="moveDownSection"
    >
    </sectionActions>
  </div>
</template>

<script lang="ts" setup>
  import mjmlColumn from "./mjmlColumn.vue";
  import sectionActions from "./sectionActions.vue";
  import CreateBlockAPI from "@/forms/components/SavedContainer/SavedBlocksAPI/_internal/CreateBlockAPI.vue";

  import { TopolSection } from "@/schemas/templateSchema";

  import { useNotificationsStore } from "@/stores/notifications";
  import { useRolesStore } from "@/stores/roles";
  import { useDraggingStore } from "@/stores/draggingStore";
  import { useSectionVariantsStore } from "@/stores/sectionVariantsStore";
  import { useMjmlStore } from "@/stores/mjmlStore";
  import { useCommentsStore } from "@/stores/commentsStore";
  import { useSavedBlocksStore } from "@/stores/savedBlocksStore";
  import useSyncedSections from "@/composables/useBlocksFromAPI/useSyncedSections";
  import { api } from "@/api";

  import { TConfirmationModal } from "@topol/twig-ui";
  import { useClipboard } from "@vueuse/core";
  import { useI18n } from "vue-i18n";

  import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
  import { sanitizeBold } from "@/utils/sanitizeBold";

  const { copy, copied } = useClipboard();

  const props = defineProps({
    index: {
      type: Number,
      required: true,
    },
  });

  const emits = defineEmits(["deleteSection"]);

  const isHovered = ref(false);
  const blockIsHover = ref(false);
  const contentHovered = ref(false);
  const mjSection = ref();
  const confirmationModalUnsaved = ref();
  const syncedLangConfirmModal = ref();
  const syncedLangConfirmPicked = ref("");
  const syncedLangConfirmOthers = ref("");

  const onConfirmSyncedLangSave = (payload: {
    pickedLang: string;
    otherLangs: string[];
    resolve: (confirmed: boolean) => void;
  }) => {
    if (!syncedLangConfirmModal.value) {
      payload.resolve(true);
      return;
    }
    syncedLangConfirmPicked.value = payload.pickedLang;
    syncedLangConfirmOthers.value = payload.otherLangs.join(", ");
    syncedLangConfirmModal.value.open().then((result: string) => {
      payload.resolve(result !== "cancel");
    });
  };
  const modalIsOpen = ref(false);
  const creatingSavedBlock = ref(false);
  const creatingSynedRow = ref(false);
  const showPaste = ref(false);

  const notificationsStore = useNotificationsStore();
  const rolesStore = useRolesStore();
  const draggingStore = useDraggingStore();
  const sectionVariantsStore = useSectionVariantsStore();
  const mjmlStore = useMjmlStore();
  const commentsStore = useCommentsStore();
  const savedBlocksStore = useSavedBlocksStore();
  const { saveEditingRow, updateSyncedSections } = useSyncedSections();
  const { t } = useI18n();

  const syncedLangConfirmMessage = computed(() =>
    sanitizeBold(
      t("forms.synced_lang_save_confirm", {
        lang: syncedLangConfirmPicked.value,
        others: syncedLangConfirmOthers.value,
      })
    )
  );

  const bgWidth = computed(() => {
    return mjmlStore.getBodyWidth;
  });

  const section = computed(() => {
    return mjmlStore.getMjml().children[0].children[
      props.index
    ] as TopolSection;
  });

  const sectionLength = computed(() => {
    return mjmlStore.getMjml().children[0].children.length;
  });

  const hasVariants = computed(() => {
    if (!section.value) {
      return false;
    }

    return (
      sectionVariantsStore.getSectionVariants(section.value.uid).length > 0
    );
  });

  const isActiveVariant = computed(() => {
    if (hasVariants.value) {
      return (
        sectionVariantsStore.getActiveVariantUid(section.value?.uid) ===
        section.value?.uid
      );
    }
    return false;
  });

  const isSyncedRow = computed(() => {
    if (section.value && section.value.syncedId !== undefined) return true;

    return false;
  });

  const isEditing = computed(() => {
    if (!section.value) {
      return false;
    }
    return savedBlocksStore.editingRowUid === section.value.uid;
  });

  const disableUpMovement = computed(() => {
    if (hasVariants.value) {
      return true;
    }
    return mjmlStore.isFirstSection(props.index);
  });

  const disableDownMovement = computed(() => {
    if (hasVariants.value) {
      return true;
    }
    return mjmlStore.isLastSection(props.index);
  });

  const showSectionActions = computed(() => {
    return isHovered.value === true && blockIsHover.value === false;
  });

  const isLocked = computed(() => {
    if (!section.value) {
      return false;
    }

    let isLocked = section.value.attributes.locked;

    if (isLocked === undefined) {
      isLocked = false;
    }

    return isLocked;
  });

  const isMovableForCurrentPermissions = computed(() => {
    if (
      !isLocked.value ||
      (isLocked.value && rolesStore.permissions.canEditLockedSections)
    ) {
      return true;
    }

    return false;
  });

  const sectionStylesDiv = computed(() => {
    const globalGradient = section.value?.attributes["background-color"]
      ? ""
      : mjmlStore.getBaseAttrString("mj-class", "gradient") || "";
    const bgImage = section.value?.attributes["background-url"]
      ? `url(${section.value?.attributes["background-url"]})`
      : section.value?.attributes["gradient"] || globalGradient;
    return {
      backgroundColor:
        (section.value?.attributes["background-color"] ||
          mjmlStore.getBaseAttrString("mj-class", "background-color")) &&
        section.value?.attributes["full-width"]
          ? section.value.attributes["background-color"]
          : "",
      backgroundImage:
        bgImage && section.value?.attributes["full-width"] ? bgImage : "",
    };
  });

  const fullWidthTableStyles = computed(() => {
    const globalGradient = section.value?.attributes["background-color"]
      ? ""
      : mjmlStore.getBaseAttrString("mj-class", "gradient") || "";
    const bgImage = section.value?.attributes["background-url"]
      ? `url(${section.value?.attributes["background-url"]})`
      : section.value?.attributes["gradient"] || globalGradient;
    return {
      backgroundColor:
        section.value?.attributes["background-color"] ||
        mjmlStore.getBaseAttrString("mj-class", "background-color"),
      backgroundImage: bgImage ? bgImage : "",
      backgroundSize: section.value?.attributes["background-size"]
        ? section.value.attributes["background-size"]
        : "cover",
      backgroundRepeat:
        section.value?.attributes["background-repeat"] || "repeat",
      borderRadius: section.value?.attributes["border-radius"] || 0,
    };
  });

  const normalTableStyles = computed(() => {
    const globalGradient = section.value?.attributes["background-color"]
      ? ""
      : mjmlStore.getBaseAttrString("mj-class", "gradient") || "";
    const bgImage = section.value?.attributes["background-url"]
      ? `url(${section.value?.attributes["background-url"]})`
      : section.value?.attributes["gradient"] || globalGradient;
    return {
      maxWidth: bgWidth.value + "px",
      backgroundColor:
        section.value?.attributes["background-color"] ||
        mjmlStore.getBaseAttrString("mj-class", "background-color"),
      backgroundImage: bgImage ? bgImage : "",
      backgroundSize: section.value?.attributes["background-size"]
        ? section.value.attributes["background-size"]
        : "cover",
      backgroundRepeat:
        section.value?.attributes["background-repeat"] || "repeat",
      borderRadius: section.value?.attributes["border-radius"] || 0,
    };
  });

  const sectionStyles = computed(() => {
    const style: {
      padding: string;
      border?: string;
      "border-top"?: string;
      "border-bottom"?: string;
      "border-left"?: string;
      "border-right"?: string;
      "border-radius"?: string;
    } = {
      padding: "20px 0px",
    };

    if (!section.value) return style;

    if (section.value.attributes["padding"])
      style.padding = section.value.attributes["padding"];

    if (section.value.attributes.border)
      style.border = section.value.attributes.border;

    if (section.value.attributes["border-top"])
      style["border-top"] = section.value.attributes["border-top"];

    if (section.value.attributes["border-bottom"])
      style["border-bottom"] = section.value.attributes["border-bottom"];

    if (section.value.attributes["border-left"])
      style["border-left"] = section.value.attributes["border-left"];

    if (section.value.attributes["border-right"])
      style["border-right"] = section.value.attributes["border-right"];

    return style;
  });

  const isActive = computed(() => {
    return mjmlStore.isActiveSection({
      sectionIndex: props.index,
    });
  });

  const layout = computed(() => {
    if (section.value && section.value.attributes["full-width"]) {
      return "full";
    } else {
      return "normal";
    }
  });

  const isHidden = computed(() => {
    if (mjmlStore.isMobileFirstEnabled && section.value) {
      const cssClass = section.value.attributes["css-class"];
      const isMobileView = mjmlStore.isMobileView;

      if (cssClass === "hide_section_on_mobile" && isMobileView) {
        return true;
      } else if (cssClass === "hide_section_on_desktop" && !isMobileView) {
        return true;
      }
    }

    return false;
  });

  const bluredOrNotHidden = computed(() => {
    if (!section.value) {
      return false;
    }
    if (isHidden.value) {
      if (!mjmlStore.blurHiddenElements) {
        return true;
      }

      return false;
    }

    return true;
  });

  const hiddenStyle = computed(() => {
    if (isHidden.value && !mjmlStore.blurHiddenElements) {
      return {
        opacity: "25%",
      };
    }

    return {};
  });

  const activeSection = computed(() => {
    if (mjmlStore.selectedSection)
      return mjmlStore.selectedSection.sectionIndex;
    return mjmlStore.selectedBlock?.sectionIndex;
  });

  async function mouseover() {
    isHovered.value = true;
    showPaste.value = true;
  }

  function click(event: any) {
    if (
      mjSection.value &&
      mjSection.value.querySelector(".variants-toggle") &&
      event
        .composedPath()
        .includes(mjSection.value.querySelector(".variants-toggle"))
    ) {
      return;
    }

    mjmlStore.selectSection({
      sectionIndex: props.index,
    });
  }

  function createSyncedSection() {
    creatingSynedRow.value = true;
    modalIsOpen.value = true;
  }

  function syncedSection(syncedRow: any) {
    creatingSynedRow.value = false;
    modalIsOpen.value = false;
    if (syncedRow) {
      mjmlStore.getSelectedSectionByIndex(props.index).syncedId = syncedRow.id;
    }
  }

  function saveSection() {
    if (savedBlocksStore.savedBlocks === true) {
      creatingSavedBlock.value = true;
      modalIsOpen.value = true;
    } else {
      window.$event.emit("saveSection", {
        definition: mjmlStore.getSelectedSectionByIndex(props.index),
        translation: mjmlStore.getSectionTranslationByIndex(props.index),
      });
    }
  }

  function closeSavedBlockModal() {
    creatingSavedBlock.value = false;
    modalIsOpen.value = false;
  }

  function copySection() {
    mjmlStore.duplicateSection({
      sectionIndex: props.index,
    });
  }

  function unlinkSyncedSection() {
    mjmlStore.unlinkSyncedSection({
      sectionIndex: props.index,
    });
  }

  async function copyToClipboard() {
    try {
      await navigator.clipboard.readText();
      copy(JSON.stringify(mjmlStore.getSelectedSectionByIndex(props.index)));
    } catch (e) {
      notificationsStore.createNotification({
        type: "error",
        text: t("layout.clipboard_not_allowed"),
      });
      return;
    }
  }

  function deleteSection() {
    emits("deleteSection", props.index);
    mjmlStore
      .deleteSectionPromise({
        sectionIndex: props.index,
      })
      .catch(() => {
        notificationsStore.createNotification({
          type: "error",
          text: t("layout.delete_section_error"),
        });
      });
  }

  function moveUpSection() {
    if (props.index - 1 >= 0) {
      mjmlStore.reorderSections({
        newIndex: props.index - 1,
        oldIndex: props.index,
        arrowMovement: true,
      });
    }
  }

  function moveDownSection() {
    if (props.index + 1 <= sectionLength.value) {
      mjmlStore.reorderSections({
        newIndex: props.index + 1,
        oldIndex: props.index,
        arrowMovement: true,
      });
    }
  }

  watch(activeSection, async (newIndex) => {
    if (
      savedBlocksStore.editingRowUid &&
      newIndex !==
        mjmlStore.getSectionIndexByUid(savedBlocksStore.editingRowUid) &&
      section.value?.uid === savedBlocksStore.editingRowUid
    ) {
      modalIsOpen.value = true;
      const confirmation = await confirmationModalUnsaved.value.open();
      if (confirmation === "cancel") {
        savedBlocksStore.editingRowUid = undefined;
        updateSyncedSections();
        modalIsOpen.value = false;
        return;
      }
      await saveEditingRow();
      modalIsOpen.value = false;
    }
  });

  function onBlockHovered() {
    if (blockIsHover.value === false) {
      blockIsHover.value = true;
    }
  }

  function onBlockHoveredOff() {
    if (blockIsHover.value === true) {
      blockIsHover.value = false;
    }
  }

  function onConversationHoveredOn() {
    if (
      isHovered.value === false &&
      commentsStore.hoverOverElement({
        type: "section",
        sectionIndex: props.index,
      })
    ) {
      isHovered.value = true;
    }
  }

  function onConversationHoveredOff() {
    if (isHovered.value === true) {
      isHovered.value = false;
    }
  }

  onMounted(() => {
    window.$event.on("blockHovered", onBlockHovered);
    window.$event.on("blockHoveredOff", onBlockHoveredOff);
    window.$event.on("conversationHoveredOn", onConversationHoveredOn);
    window.$event.on("conversationHoveredOff", onConversationHoveredOff);
  });

  watch(
    isEditing,
    (editing) => {
      if (editing) {
        window.$event.on("confirmSyncedLangSave", onConfirmSyncedLangSave);
      } else {
        window.$event.off("confirmSyncedLangSave", onConfirmSyncedLangSave);
      }
    },
    { immediate: true }
  );

  onBeforeUnmount(() => {
    window.$event.off("blockHovered", onBlockHovered);
    window.$event.off("blockHoveredOff", onBlockHoveredOff);
    window.$event.off("conversationHoveredOn", onConversationHoveredOn);
    window.$event.off("conversationHoveredOff", onConversationHoveredOff);
    window.$event.off("confirmSyncedLangSave", onConfirmSyncedLangSave);
  });
</script>

<style scoped>
  .shadow-out {
    box-shadow: 5px 5px 20px 5px rgba(0, 0, 0, 0.03);
  }

  .shadow-in {
    box-shadow: 5px 5px 20px 5px rgba(0, 0, 0, 0.03);
  }
</style>
